import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Eye, EyeOff } from "lucide-react";
import { answerPdfPassword, useCurrentPdfPasswordRequest, type PdfPasswordRequest } from "@/hooks/usePdfPasswordPrompt";

/**
 * Asks for the password of a locked PDF the moment it's chosen, in any
 * PDF tool. Mounted once, globally, in Layout.tsx -- FileDropZone asks
 * for passwords through the usePdfPasswordPrompt store, so every tool
 * gets this without wiring anything up itself.
 */
export function PdfPasswordDialog() {
  const request = useCurrentPdfPasswordRequest();
  return (
    <Dialog open={request !== null} onOpenChange={(open) => { if (!open) answerPdfPassword(null); }}>
      <DialogContent className="sm:max-w-md">
        {/* keyed by request so the input starts empty for each prompt */}
        {request && <PasswordForm key={request.id} request={request} />}
      </DialogContent>
    </Dialog>
  );
}

function PasswordForm({ request }: { request: PdfPasswordRequest }) {
  const [password, setPassword] = useState("");
  // Show/hide, off by default: a password someone is typing from a document or a chat message is
  // easy to get wrong blind (QA MP-11).
  const [visible, setVisible] = useState(false);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (password) answerPdfPassword(password);
      }}
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4" /> This PDF is password-protected</DialogTitle>
        <DialogDescription>
          Enter the password for <span className="font-medium text-foreground break-all">{request.fileName}</span> to continue.
          It's unlocked right here in your browser — the file and password are never uploaded — and your result
          won't have a password.
        </DialogDescription>
      </DialogHeader>
      <div className="my-4">
        <div className="relative">
          <Input
            type={visible ? "text" : "password"}
            autoFocus
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="PDF password"
            aria-label="PDF password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={request.previousAttemptWasWrong}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? "Hide password" : "Show password"}
            aria-pressed={visible}
            title={visible ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
          >
            {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {request.previousAttemptWasWrong && (
          <p className="text-sm text-destructive mt-2" role="alert">That password didn't work — please try again.</p>
        )}
      </div>
      <DialogFooter className="gap-2 sm:gap-0">
        <Button type="button" variant="outline" onClick={() => answerPdfPassword(null)}>{request.singleFile ? "Cancel" : "Skip this file"}</Button>
        <Button type="submit" disabled={!password}>Unlock</Button>
      </DialogFooter>
    </form>
  );
}
